import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./PokerTablePage.css";
import {
  getPokerSocket,
  pokerPing,
  pokerJoinRoom,
  pokerDealHand,
  pokerFlop,
  pokerTurn,
  pokerRiver,
  pokerShowdown,
  pokerNextHand,
  pokerAction,
  pokerShowCards,
  pokerSitOut,
  pokerReturnToSeat,
  pokerLeaveRoom,
  pokerTourneyPause,
  pokerTourneyResume,
  pokerTourneyNextLevel,
  pokerTourneyPrevLevel,
  pokerTourneyResetLevel,
} from "../ws/pokerClient";

import MatematronModal from "../../components/ui/MatematronModal";
import HandResultPanel from "./table/HandResultPanel";

import { dealerStepLabelFromStreet, humanizePokerErrorPL } from "./table/helpers";
import { ThemeContext } from "../../context/ThemeContext";

import PokerTableHeaderBar from "./PokerTableHeaderBar";
import PokerTableViewVisual from "./PokerTableViewVisual";
import PokerTableViewList from "./PokerTableViewList";

export default function PokerTablePage() {
  const { code } = useParams();
  const nav = useNavigate();
  const socket = useMemo(() => getPokerSocket(), []);

  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === "dark";

  const [me, setMe] = useState(null);
  const [room, setRoom] = useState(null);
  const [myHole, setMyHole] = useState(null);
  const [showdownHoles, setShowdownHoles] = useState(null);

  const [err, setErr] = useState("");
  const [joined, setJoined] = useState(false);

  const [raiseTo, setRaiseTo] = useState(0);

  const [tableView, setTableView] = useState("visual");
  const [stackViewMode, setStackViewMode] = useState("chips"); // "visual" | "list"

  // ✅ DEV/ADMIN: podgląd liczby miejsc (null = auto, 2..8 = wymuś)
  const [previewSeats, setPreviewSeats] = useState(null);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  // ✅ overlay pomiary
  const visualStageRef = useRef(null);
  const railRef = useRef(null);
  const maxRailHRef = useRef(0);
  const railStableHRef = useRef(0); // ✅ wysokość raila, która może rosnąć i maleć (bez "pompowania")
  const lastStageSizeRef = useRef({ w: 0, h: 0 });

  const [railTopPx, setRailTopPx] = useState(null);

  // ✅ karta (zewnętrzny kontener) musi się rozszerzać: base + overflow
  const [railPadBottomPx, setRailPadBottomPx] = useState(0);

  // ✅ Visual ma dostać tylko overflow (bez base), stabilnie
  const [extraBottomPx, setExtraBottomPx] = useState(0);

  // ✅ NEW: ile px trzeba “omijać” na dole (żeby rywale nie wchodzili pod panel akcji)
  const [railAvoidPx, setRailAvoidPx] = useState(0);

  // ✅ stabilizacja pomiarów (żeby nie "trzęsło")
  const rafIdRef = useRef(0);
  const lastTopRef = useRef(-1);
  const lastPadRef = useRef(-1);
  const lastExtraRef = useRef(-1);
  const lastAvoidRef = useRef(-1);

  const tableCode = String(code || "").trim().toUpperCase();

  // ✅ zapamiętujemy stacki na start rozdania, żeby policzyć bilans (after - before)
  const handStartStacksRef = useRef({ handId: null, stacks: {} });

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const ping = await pokerPing();
        if (!alive) return;
        if (ping?.ok) setMe(ping.user || null);
      } catch {}

      try {
        const res = await pokerJoinRoom(tableCode);
        if (!alive) return;

        if (!res?.ok) {
          setErr(res?.error || "join_failed");
          setJoined(false);
          return;
        }

        setJoined(true);
        setErr("");
      } catch (e) {
        if (!alive) return;
        setErr(String(e?.message || e));
        setJoined(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [tableCode]);

  useEffect(() => {
    const onRoomState = (payload) => {
      if (!payload?.ok) return;
      setRoom(payload.room);
      setErr("");
    };

    const onHole = (payload) => {
      if (!payload?.ok) return;
      setMyHole(payload.hole || null);
    };

    const onShowdown = (payload) => {
      if (!payload?.ok) return;
      setShowdownHoles(payload.holesBySeat || null);
    };

    socket.on("POKER:ROOM_STATE", onRoomState);
    socket.on("POKER:YOUR_HOLE", onHole);
    socket.on("POKER:SHOWDOWN_REVEAL", onShowdown);

    return () => {
      socket.off("POKER:ROOM_STATE", onRoomState);
      socket.off("POKER:YOUR_HOLE", onHole);
      socket.off("POKER:SHOWDOWN_REVEAL", onShowdown);
    };
  }, [socket]);

  const game = room?.game;
  const seats = game?.seats || [];
  const seatState = game?.seatState || {};

  // ✅ zapisz stacki na start nowego rozdania
  useEffect(() => {
    if (!game?.handId) return;

    const stacks = {};
    (game?.seats || []).forEach((s) => {
      const key = String(s.seat);
      const st = game?.seatState?.[key];
      const stack = Number(st?.stack);
      if (Number.isFinite(stack)) stacks[key] = stack;
    });

    handStartStacksRef.current = { handId: game.handId, stacks };
  }, [game?.handId]);
  const betting = game?.betting || {};
  const lastActionBySeat = betting.lastActionBySeat || {};
  const board = game?.board || [];

  useEffect(() => {
    setShowdownHoles(null);
  }, [game?.handId]);

  const handResult = game?.handResult || null;
  const winners = Array.isArray(handResult?.winners) ? handResult.winners.map(Number) : [];
  const winnersSet = new Set(winners);
  const bySeat = handResult?.bySeat || {};
  const payouts = handResult?.payouts || {};

  // ✅ payouts z backendu bywają tylko dla zwycięzców; policz bilans dla wszystkich seatów: (stack_after - stack_before)
  const computedPayouts = useMemo(() => {
    const raw = payouts || {};
    const hasAnyNegative = Object.values(raw).some((v) => Number(v) < 0);
    if (hasAnyNegative) return raw;

    const start = handStartStacksRef.current;
    const startStacks = start?.handId === game?.handId ? start.stacks : {};

    const out = {};
    (seats || []).forEach((s) => {
      const key = String(s.seat);
      const before = Number(startStacks?.[key]);
      const after = Number(seatState?.[key]?.stack);
      if (!Number.isFinite(before) || !Number.isFinite(after)) return;
      out[key] = after - before;
    });

    // fallback: jeśli backend coś podał, a my nie policzyliśmy
    Object.keys(raw).forEach((k) => {
      if (out[k] == null) out[k] = Number(raw[k] || 0);
    });

    return out;
  }, [payouts, seats, seatState, game?.handId]);
  const showHandResult = !!handResult && (game?.street === "showdown" || game?.street === "complete");

  const myUserId = me?._id ? String(me._id) : null;
  const mySeatObj = myUserId ? seats.find((s) => String(s.userId) === myUserId) : null;
  const mySeatNo = mySeatObj?.seat ?? null;
  const mySt = mySeatNo != null ? seatState[String(mySeatNo)] || {} : null;

  const canRevealPhase = game?.street === "showdown" || game?.street === "complete";
  const mySeatKey = mySeatNo != null ? String(mySeatNo) : null;
  const hasPublicCards = !!(mySeatKey && showdownHoles?.[mySeatKey] && showdownHoles[mySeatKey].length === 2);

  const dealerSeat = game?.dealerSeat ?? null;
  const isDealer = mySeatNo != null && dealerSeat != null && Number(mySeatNo) === Number(dealerSeat);

  const currentBet = Number(betting.currentBet || 0);
  const myCommittedStreet = Number(mySt?.committedStreet || 0);
  const toCall = Math.max(0, currentBet - myCommittedStreet);

  const actionSeat = betting.actionSeat ?? null;
  const isMyTurn = mySeatNo != null && actionSeat != null && Number(actionSeat) === Number(mySeatNo);

  const canAdvance = !!betting.canAdvanceStreet;
  const waitingForDealer = actionSeat == null && !!game && game.street !== "complete" && canAdvance;
  const dealerStepLabel = dealerStepLabelFromStreet(game?.street);

  const hasBet = currentBet > 0;
  const passiveType = toCall > 0 ? "CALL" : "CHECK";
  const passiveLabel = toCall > 0 ? `Wyrównaj (call) — ${toCall}` : "Poczekaj (check)";

  const aggressiveType = hasBet ? "RAISE" : "BET";
  const aggressiveLabel = hasBet ? "Przebij (raise)" : "Postaw (bet)";

  const minRaiseTo = Number(betting.minRaiseTo || 0);
  const bb = Number(game?.settings?.bb || 2);

  const maxRaiseTo = mySeatNo != null ? Number(mySt?.committedStreet || 0) + Number(mySt?.stack || 0) : 0;
  const minAllowedTo = hasBet ? minRaiseTo : bb;
  const clampedTo = Math.max(minAllowedTo, Math.min(Number(raiseTo || 0), maxRaiseTo || 0));

  useEffect(() => {
    if (!game || mySeatNo == null) return;
    const next = hasBet
      ? Math.max(minRaiseTo || 0, Math.min(minRaiseTo || 0, maxRaiseTo || 0))
      : Math.max(bb, Math.min(bb, maxRaiseTo || bb));
    setRaiseTo(next);
    // eslint-disable-next-line
  }, [game?.handId, game?.street]);

  const doDealerCall = async (fn) => {
    setErr("");
    const res = await fn(tableCode);
    if (!res?.ok) setErr(res?.error || "error");
  };

  const doAction = async (type, amount) => {
    setErr("");
    const res = await pokerAction(tableCode, type, amount);
    if (!res?.ok) setErr(res?.error || "error");
  };

  const doShowCards = async () => {
      setErr("");
      const res = await pokerShowCards(tableCode);
      if (!res?.ok) setErr(res?.error || "error");
    };

    const doTourneyPause = async () => {
    const res = await pokerTourneyPause(tableCode);
    if (!res?.ok) console.warn("TOURNEY_PAUSE failed:", res);
  };

  const doTourneyResume = async () => {
    const res = await pokerTourneyResume(tableCode);
    if (!res?.ok) console.warn("TOURNEY_RESUME failed:", res);
  };

  const doTourneyNextLevel = async () => {
    const res = await pokerTourneyNextLevel(tableCode);
    if (!res?.ok) console.warn("TOURNEY_NEXT_LEVEL failed:", res);
  };

  const doTourneyPrevLevel = async () => {
    const res = await pokerTourneyPrevLevel(tableCode);
    if (!res?.ok) console.warn("TOURNEY_PREV_LEVEL failed:", res);
  };

  const doTourneyResetLevel = async () => {
    const res = await pokerTourneyResetLevel(tableCode);
    if (!res?.ok) console.warn("TOURNEY_RESET_LEVEL failed:", res);
  };

  const doToggleSitOut = async () => {
    if (mySeatNo == null) return;

    setErr("");
    const isOut = !!mySt?.sittingOut;

    const res = isOut ? await pokerReturnToSeat(tableCode) : await pokerSitOut(tableCode);
    if (!res?.ok) setErr(res?.error || "error");
  };

  const doLeaveConfirmed = async () => {
    setErr("");
    const res = await pokerLeaveRoom();
    if (!res?.ok) {
      setErr(res?.error || "error");
      return;
    }
    nav("/poker");
  };

  const tournamentComplete = !!game?.tournamentComplete;
  const tournamentWinnerSeat = game?.tournamentWinnerSeat ?? null;

  const dealerNext = (() => {
    if (!game) return { label: "Czekam na grę…", disabled: true, onClick: null };

    const street = game.street;
    const hasAnyHand = game.handId && game.handId > 0;

    if (!hasAnyHand) {
      if (tournamentComplete) return { label: "Turniej zakończony", disabled: true, onClick: null };
      return { label: "Rozdaj", disabled: false, onClick: () => doDealerCall(pokerNextHand) };
    }

    if (street === "complete") {
      if (tournamentComplete) return { label: "Turniej zakończony", disabled: true, onClick: null };
      return { label: "Następne rozdanie", disabled: false, onClick: () => doDealerCall(pokerNextHand) };
    }

    if (street === "preflop") return { label: "Flop", disabled: !canAdvance, onClick: () => doDealerCall(pokerFlop) };
    if (street === "flop") return { label: "Turn", disabled: !canAdvance, onClick: () => doDealerCall(pokerTurn) };
    if (street === "turn") return { label: "River", disabled: !canAdvance, onClick: () => doDealerCall(pokerRiver) };
    if (street === "river")
      return { label: "Showdown (odkryj)", disabled: !canAdvance, onClick: () => doDealerCall(pokerShowdown) };
    if (street === "showdown")
      return { label: "Zakończ rozdanie", disabled: false, onClick: () => doDealerCall(pokerShowdown) };

    return { label: "Dalej", disabled: true, onClick: null };
  })();

  const isRoomNotFound = err === "room_not_found";

  const canAct = !!(mySeatNo != null && mySt?.inHand && isMyTurn);
  const foldDisabled = !canAct;
  const passiveDisabled = !canAct;
  const aggressiveDisabled = !canAct || maxRaiseTo <= 0 || clampedTo < minAllowedTo || clampedTo <= 0;

  const potVal = Number(betting.pot || 0);

  // ✅ top raila + paddingBottom = overflow raila poza dół stage (stabilnie: bottom vs bottom)
  useLayoutEffect(() => {
    if (tableView !== "visual") return;

    const stage = visualStageRef.current;
    if (!stage) return;

    const potEl = stage.querySelector(".ptv-pot-block");
    if (!potEl) return;

    const baseBottomSpace = 14;
    const buffer = 2;

    const compute = () => {
      const stageRect = stage.getBoundingClientRect();
      const potRect = potEl.getBoundingClientRect();

      const gap = 8;
      const nextTop = Math.max(0, Math.round(potRect.bottom - stageRect.top + gap));

      if (nextTop !== lastTopRef.current) {
        lastTopRef.current = nextTop;
        setRailTopPx(nextTop);
      }

      const railElNow = railRef.current;
      if (!railElNow) {
        if (baseBottomSpace !== lastPadRef.current) {
          lastPadRef.current = baseBottomSpace;
          setRailPadBottomPx(baseBottomSpace);
        }
        if (0 !== lastExtraRef.current) {
          lastExtraRef.current = 0;
          setExtraBottomPx(0);
        }
        if (0 !== lastAvoidRef.current) {
          lastAvoidRef.current = 0;
          setRailAvoidPx(0);
        }
        return;
      }

      const railHeightNow = railElNow.getBoundingClientRect().height;

      // reset cache, jeśli stage mocno zmienił rozmiar (resize/orientacja)
      const w = Math.round(stageRect.width);
      const h = Math.round(stageRect.height);
      const prev = lastStageSizeRef.current;
      if (Math.abs(prev.w - w) > 8 || Math.abs(prev.h - h) > 8) {
        maxRailHRef.current = 0;
        railStableHRef.current = 0;
        lastStageSizeRef.current = { w, h };
      }

      // ✅ stabilna wysokość raila: rośnie od razu, maleje płynnie (żeby nie drgało)
// (to rozwiązuje problem: po zwinięciu panelu akcji margines ma wrócić)
const railNow = Math.round(railHeightNow);
const prevStable = railStableHRef.current || railNow;

// rośnięcie: od razu do góry
let nextStable = railNow >= prevStable ? railNow : prevStable;

// kurczenie: jeśli spadek jest wyraźny, schodzimy płynnie w dół (histereza)
const drop = prevStable - railNow;
if (drop > 6) {
  const alpha = drop > 40 ? 0.45 : 0.25; // duży spadek = szybciej
  nextStable = Math.round(prevStable * (1 - alpha) + railNow * alpha);
}

// gdy rail jest zamknięty (canAct=false), nie trzymaj „starego” – wróć do bieżącego
if (!canAct) nextStable = railNow;

railStableHRef.current = nextStable;
const railHeightStable = nextStable;

      const sceneEl = stage.querySelector(".ptv-scene");
      const sceneRect = sceneEl ? sceneEl.getBoundingClientRect() : null;
      const baseHeight = sceneRect ? sceneRect.height : stageRect.height;

      const overflow = Math.max(0, Math.round(nextTop + railHeightStable - baseHeight + buffer));

      const nextPad = baseBottomSpace;
      if (nextPad !== lastPadRef.current) {
        lastPadRef.current = nextPad;
        setRailPadBottomPx(nextPad);
      }

      if (overflow !== lastExtraRef.current) {
        lastExtraRef.current = overflow;
        setExtraBottomPx(overflow);
      }

      // ✅ NEW: omijanie raila (TYLKO gdy masz turę) – dynamicznie zależnie od ekranu i liczby graczy
      const nSeats = Array.isArray(seats) ? seats.length : 0;
      const stageWNow = stageRect.width || 0;
      const stageHNow = stageRect.height || 0;
      const crowdedLocal = stageWNow < 560 || stageHNow > stageWNow;

      // procentowy bufor od wysokości sceny (na telefonie większy)
      const pctBuf = crowdedLocal ? 0.10 : 0.06;

      // korekta zależna od liczby graczy (więcej graczy = większy docisk, trzeba mocniej omijać)
      const seatsBuf = nSeats >= 8 ? 16 : nSeats >= 6 ? 8 : 0;

      // ✅ zawsze trzymaj jakąś strefę zakazaną na dole, żeby seaty nie wchodziły na board
      const minAvoid = Math.round(baseHeight * (crowdedLocal ? 0.18 : 0.12)); // stały, ale ciaśniejszy

      // jeśli masz turę, rail może się rozbudować – wtedy omijamy go realnie
const avoidRaw = Math.round(railHeightStable + 14 + baseHeight * pctBuf + seatsBuf);

// ✅ na sztywno: gdy nie masz tury – trzymaj tylko minimalny, stały avoid (żeby layout był kompaktowy)
const avoid = canAct
  ? Math.min(Math.max(minAvoid, avoidRaw), Math.round(baseHeight * 0.45))
  : minAvoid;

      if (avoid !== lastAvoidRef.current) {
        lastAvoidRef.current = avoid;
        setRailAvoidPx(avoid);
      }
    };

    const schedule = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(compute);
    };

    schedule();

    const RO = window.ResizeObserver;
    const ro = RO ? new RO(() => schedule()) : null;

    ro?.observe(stage);
    ro?.observe(potEl);
    if (railRef.current) ro?.observe(railRef.current);

    window.addEventListener("resize", schedule);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [tableView, game?.street, betting?.pot, betting?.minRaiseTo, currentBet, canAct, seats?.length]);

  return (
    <div style={{ padding: 18, minHeight: "calc(100vh - 36px)", display: "flex", justifyContent: "center" }}>
      <div className="glass-container" style={{ width: "100%", maxWidth: 980 }}>
        <PokerTableHeaderBar
          tableCode={tableCode}
          joined={joined}
          game={game}
          actionSeat={actionSeat}
          mySeatNo={mySeatNo}
          mySt={mySt}
          tournamentComplete={tournamentComplete}
          tournamentWinnerSeat={tournamentWinnerSeat}
          tableView={tableView}
          setTableView={setTableView}
          doToggleSitOut={doToggleSitOut}
          setConfirmLeaveOpen={setConfirmLeaveOpen}
          isDealer={isDealer}
          dealerNext={dealerNext}
          nav={nav}
          onTourneyPause={doTourneyPause}
          onTourneyResume={doTourneyResume}
          onTourneyNextLevel={doTourneyNextLevel}
          onTourneyPrevLevel={doTourneyPrevLevel}
          onTourneyResetLevel={doTourneyResetLevel}
        />

        <HandResultPanel
          show={!!handResult && (game?.street === "showdown" || game?.street === "complete")}
          winners={winners}
          winnersSet={winnersSet}
          bySeat={bySeat}
          payouts={computedPayouts}
        />

        {err ? (
          <div className="poker-card" style={{ marginTop: 12, border: "1px solid rgba(255,80,80,0.6)" }}>
            <b style={{ color: "#ff6b6b" }}>Błąd:</b> {humanizePokerErrorPL(err)}
            {isRoomNotFound ? (
              <div style={{ marginTop: 8, opacity: 0.9 }}>
                Ten pokój nie istnieje (albo backend był restartowany). Utwórz pokój w lobby i wejdź ponownie.
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-secondary" onClick={() => nav("/poker")}>
                    Wróć do lobby
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {tableView === "visual" ? (
          <PokerTableViewVisual
          stackViewMode={stackViewMode}
          setStackViewMode={setStackViewMode}
          bbAmount={bb}
            isDark={isDark}
            me={me}
            previewSeats={previewSeats}
            setPreviewSeats={setPreviewSeats}
            betting={betting}
            currentBet={currentBet}
            visualStageRef={visualStageRef}
            railRef={railRef}
            railTopPx={railTopPx}
            railPadBottomPx={railPadBottomPx}
            extraBottomPx={extraBottomPx}
            railAvoidPx={railAvoidPx}
            seats={seats}
            seatState={seatState}
            mySeatNo={mySeatNo}
            dealerSeat={dealerSeat}
            sbSeat={game?.sbSeat ?? null}
            bbSeat={game?.bbSeat ?? null}
            actionSeat={actionSeat}
            lastActionBySeat={lastActionBySeat}
            board={board}
            myHole={myHole}
            showdownHoles={showdownHoles}
            street={game?.street}
            handId={Number(game?.handId || 0)}
            canAct={canAct}
            isMyTurn={isMyTurn}
            isDealer={isDealer}
            waitingForDealer={waitingForDealer}
            dealerStepLabel={dealerStepLabel}
            foldDisabled={foldDisabled}
            passiveDisabled={passiveDisabled}
            aggressiveDisabled={aggressiveDisabled}
            passiveType={passiveType}
            passiveLabel={passiveLabel}
            aggressiveType={aggressiveType}
            aggressiveLabel={aggressiveLabel}
            hasBet={hasBet}
            clampedTo={clampedTo}
            minAllowedTo={minAllowedTo}
            minRaiseTo={minRaiseTo}
            maxRaiseTo={maxRaiseTo}
            bb={bb}
            potVal={potVal}
            toCall={toCall}
            setRaiseTo={setRaiseTo}
            doAction={doAction}
            doShowCards={doShowCards}
            canRevealPhase={canRevealPhase}
            hasPublicCards={hasPublicCards}
            mySt={mySt}
            // ✅ end-of-hand result
            showHandResult={showHandResult}
            bySeat={bySeat}
            payouts={computedPayouts}
          />
        ) : null}

        {tableView === "list" ? (
          <PokerTableViewList
            bbAmount={bb}
            stackViewMode={stackViewMode}
            setStackViewMode={setStackViewMode}
            betting={betting}
            currentBet={currentBet}
            board={board}
            seats={seats}
            seatState={seatState}
            mySeatNo={mySeatNo}
            actionSeat={actionSeat}
            dealerSeat={dealerSeat}
            sbSeat={game?.sbSeat ?? null}
            bbSeat={game?.bbSeat ?? null}
            showHandResult={showHandResult}
            bySeat={bySeat}
            winners={winners}
            winnersSet={winnersSet}
            payouts={computedPayouts}
            revealAll={!!game?.revealAll}
            street={game?.street}
            handId={Number(game?.handId || 0)}
            showdownHoles={showdownHoles}
            doShowCards={doShowCards}
            lastActionBySeat={lastActionBySeat}
            myHole={myHole}
            mySt={mySt}
            isMyTurn={isMyTurn}
            waitingForDealer={waitingForDealer}
            dealerStepLabel={dealerStepLabel}
            isDealer={isDealer}
            canAct={canAct}
            foldDisabled={foldDisabled}
            passiveDisabled={passiveDisabled}
            aggressiveDisabled={aggressiveDisabled}
            passiveType={passiveType}
            passiveLabel={passiveLabel}
            aggressiveType={aggressiveType}
            aggressiveLabel={aggressiveLabel}
            hasBet={hasBet}
            clampedTo={clampedTo}
            minAllowedTo={minAllowedTo}
            minRaiseTo={minRaiseTo}
            maxRaiseTo={maxRaiseTo}
            bb={bb}
            potVal={potVal}
            toCall={toCall}
            setRaiseTo={setRaiseTo}
            doAction={doAction}
          />
        ) : null}

        <MatematronModal
          open={confirmLeaveOpen}
          variant="info"
          title="Potwierdź wyjście"
          message="Na pewno chcesz wyjść ze stołu? To działanie usuwa Cię z gry (cashout)."
          primaryText="Wyjdź"
          onPrimary={() => {
            setConfirmLeaveOpen(false);
            doLeaveConfirmed();
          }}
          secondaryText="Anuluj"
          onSecondary={() => setConfirmLeaveOpen(false)}
          onClose={() => setConfirmLeaveOpen(false)}
          showBadge
          badgeText="Poker"
        />
      </div>
    </div>
  );
}
